import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ViewEncapsulation} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {CommonFieldService} from '../../../../services/common/common-field.service';
import {InternalTradeOrderViewService} from '../../../../services/internal/internal-trade-order-view.service';
import {filter, Observable, takeUntil, timer} from "rxjs";
import {ApiValidation} from "../../../../model/api/api-validation";
import {ClearingSelectionInfo} from "../../../../model/clearing/clearing-selection-info";
import {ClearingAllocationTaskService} from "../../../../services/clearing/clearing-allocation-task.service";
import {ClearingAllocationService} from "../../../../services/clearing/clearing-allocation.service";
import {ThemeService} from "../../../../services/tools/theme.service";
import {ClearingTradeInfoService} from "../../../../services/clearing/clearing-trade-info.service";
import {ClearingInfoLoadRequest} from "../../../../model/clearing/clearing-info-load-request";
import {MultiSelect} from "primeng/multiselect";
import {TradeTableBaseComponent} from "../trade/trade-table-base.component";
import {AppConfigService} from "../../../../services/app-config.service";
import {WebTradeOrderView} from "../../../../model/internal/web-trade-order-view";
import {TableColumnConfig} from "../../../../model/api/table-column-config";
import {ClearingTableConfigService} from "../../../../services/clearing/clearing-table-config.service";
import {ClearingCookieService} from "../../../../services/clearing/clearing-cookie-service";
import {ClearingSelectionService} from "../../../../services/clearing/clearing-selection.service";
import {ClearingTradeAggregWsService} from "../../../../services/clearing/ws/clearing-trade-aggreg-ws.service";
import {ClearingTableSyncWsService} from "../../../../services/clearing/ws/clearing-table-sync-ws.service";
import {
  ClearingTableConfigTradeAggregService
} from "../../../../services/clearing/clearing-table-config-trade-aggreg.service";

@Component({
  selector: 'app-clearing-trade-aggregate-table[userView]',
  templateUrl: './trade-aggregate-table.component.html',
  styleUrls: ['../clearing-table-base.component.scss', 'trade-aggregate-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeAggregateTableComponent extends TradeTableBaseComponent {

  filterWithValuesOptions: any[] = [];
  filterWithValue: any[] = [];

  @Input()
  selectionBase = false;

  protected selectionSummaryRequestVersion: number = 0;

  constructor(cdref: ChangeDetectorRef,
              allocationService: ClearingAllocationService,
              allocationTaskService: ClearingAllocationTaskService,
              protected commonFieldService: CommonFieldService,
              protected userViewService: InternalTradeOrderViewService,
              appConfigService: AppConfigService,
              clearingWsService: ClearingTradeAggregWsService,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              public translateService: TranslateService,
              public themeService: ThemeService,
              protected clearingTradeInfoService: ClearingTradeInfoService,
              public tableConfigService: ClearingTableConfigService,
              protected clearingCookieService: ClearingCookieService,
              protected clearingTableSyncWsService: ClearingTableSyncWsService,
              protected clearingSelectionService: ClearingSelectionService,
              protected clearingTableConfigTradeAggregService: ClearingTableConfigTradeAggregService) {
    super(cdref,
      allocationService,
      allocationTaskService,
      commonFieldService,
      userViewService,
      appConfigService,
      clearingWsService,
      messageService,
      confirmationService,
      translateService,
      themeService,
      tableConfigService,
      clearingCookieService,
      clearingTableSyncWsService,
      clearingSelectionService,
      clearingTradeInfoService);
    this.filterWithValue = [...appConfigService?.tradeAggregationColumns
      ?.filter(value => value.defaultIgnore && value.filterable)
      .map(value => value.field)];
    this.origin = WebTradeOrderView.ORIGIN.TRADE_AGG;
    this.transPartKey = 'clearing.trade.selection'
    this.minItemCountForSummary = 1;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  public initConfigs() {
    super.initConfigs();

    this.filterWithValuesOptions = [];
    this.appConfigService.tradeAggregationColumns.filter(value => value.filterable).forEach(value => this.filterWithValuesOptions.push(
      {
        id: value.field,
        text: value.label,
        disabled: false
      }
    ))

  }

  protected refreshTable() {

    this.filterWithValue = this.clearingCookieService.loadSelectedFilter(this.userView.id);

    this.tableConfig = this.clearingTableConfigTradeAggregService.buildTableConfig(this.viewConfig, this.visibleColumns, this.exchanges, this.filterWithValue);


    this.markForCheck();
    // this.cdref.markForCheck();
  }

  rowDblClick(event) {
    this.ctrlShift(event,false);
  }

  ctrlShift(event, forceSingle: boolean = true) {
    timer(300)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isReadOnly()
          && (this.singleAllocationEnabled() || this.splitAllocationEnabled(this.currentClearingSelectionInfosByLeg))) {
          this.allocateFromSelection(undefined, forceSingle);
        }
      });
  }

  protected getSelectionSummary(summaryRequest: ClearingInfoLoadRequest) {
    const requestVersion = ++this.selectionSummaryRequestVersion;
    summaryRequest.itemIds = this.getTrueSelectedTradeIds();
    return this.clearingTradeInfoService.getSelectionSummary(summaryRequest).pipe(
      filter(() => requestVersion === this.selectionSummaryRequestVersion)
    );
  }

  protected getSelectionInfoLegs(request: ClearingInfoLoadRequest): Observable<ApiValidation<ClearingSelectionInfo[]>> {
    request.itemIds = this.getTrueSelectedTradeIds();
    return this.clearingTradeInfoService.getSelectionInfoLegs(request);
  }

  prepareRequestBeforePost(requestToPrepare: ClearingInfoLoadRequest) {
    let request: any
    request = {
      ignoreFields: this.filterWithValue,
      itemIds: [],
      selectionBase: this.selectionBase, ...super.prepareRequestBeforePost(requestToPrepare)
    };
    if (!request.ignoreFields)
      request.ignoreFields = [];
    return request;
  }

  filterChanged(value, multOver?: MultiSelect) {
    // this.tableLoadingCount++;
    this.setLoading();
    queueMicrotask(() => {
      this.clearingCookieService.storeSelectedFilter(this.userView.id, this.filterWithValue);
      this.refreshTable();
      this.clearReloadTable(this.tableCurrentRequest);
    });
  }

  clearFilter() {
    this.filterWithValue.splice(0);
    this.filterChanged(this.filterWithValue)
  }

  protected getTrueSelectedTradeIds(
    selectedIds: string[] = this.selectedIds
  ): string[] {
    const result: string[] = [];
    if (selectedIds && selectedIds.length > 0) {
      for (const selectedId of selectedIds) {
        if (selectedId) {
          const parts = selectedId.split(',');
          for (const part of parts) {
            result.push(part);
          }
        }
      }
    }
    return result;
  }

  override colOrderChanged(event: TableColumnConfig[]) {
    this.visibleColumns = [];
    if (this.userView && event) {
      this.visibleColumns = event.filter(value => !value.hidden).map(value => value.field);
      this.clearingCookieService.storeVisibleColumns(this.userView.id, this.origin, this.visibleColumns);
    }
  }
}
